/**
 * EmailVerifyPanel.jsx — Étape de vérification email 2FA après inscription EarniConnect.
 *
 * État machine couvert : EMAIL_VERIFY
 *
 * Contrat backend réutilisé (AuthService.js existant, §9 SPEC — zéro doublon) :
 *   POST /auth/verify  { metamask, code, isRegister: true }
 *     → réponse user { isVerified: true, accessToken, ... }
 *     → dispatch EMAIL_VERIFIED(user)
 *   POST /auth/resend  { metamask }
 *     → confirmation de renvoi
 *
 * Wording imposé (anglais, casse grammaticale §13 CLAUDE.md) :
 *   "Verify your email" / "Enter the verification code" / "Verify" / "Resend code"
 *
 * Règles DA :
 *   §14   — Zéro import @mui/@material-ui
 *   §20   — Zéro littéral couleur hors theme.js
 *   §12 ter — Zéro px en dur
 *   §18 ter — Au moins 1 breakpoint responsive
 *   §12 undecies — Casse grammaticale, pas d'uppercase forcé
 *   §8.7  — Zéro Redux (le parent gère via onSuccess)
 *   §9    — Réutilise verify() + resend() de AuthService.js (zéro doublon)
 */

import React, { useState, useCallback } from 'react';
import { ACTION_TYPES } from '../hooks/useWalletStateMachine';
import { verify, resend } from '../../../services/AuthService';
import {
  WalletBody,
  WalletFooter,
  FormGroup,
  FormLabel,
  FormInput,
  InlineError,
  PrimaryBtn,
  SecondaryBtn,
  BackLink,
  StateIconWrap,
  StateTitle,
  StateMessage,
  AlertBox,
  AlertText,
} from '../styles/EarniWalletModal.styles';
import {COLORS, FONTS, SPACE, media, FONT_SIZE, FONT_WEIGHT} from '../../ui/theme';
import styled from 'styled-components';

/* ─── Styled-components locaux ──────────────────────────────────────────────── */

/** Email masqué affiché sous le titre */
const MaskedEmail = styled.p`
  margin: 0;
  font-family: ${FONTS.mono};
  font-size: ${FONT_SIZE.sm};
  font-weight: ${FONT_WEIGHT.semibold};
  color: ${COLORS.accent};
  text-align: center;
  word-break: break-all;
`;

/** Message de confirmation de renvoi */
const ResendConfirm = styled.p`
  margin: 0;
  font-family: ${FONTS.body};
  font-size: 0.8125rem;
  color: ${COLORS.cta};
  line-height: 1.4;
  text-align: center;
`;

/** Ligne de boutons footer (Resend code | Verify) */
const FooterRow = styled.div`
  display: flex;
  gap: ${SPACE.sm};
  flex-wrap: wrap;

  ${media.sm`
    flex-direction: column;
  `}
`;

/* ─── Utilitaire : masquer un email (même logique que usePrivateKeyExport) ─── */
function maskEmail(email) {
  if (!email || !email.includes('@')) return email ?? '';
  const [local, domain] = email.split('@');
  const maskedLocal  = local[0] + '*'.repeat(Math.max(1, local.length - 1));
  const domainParts  = domain.split('.');
  const maskedDomain = domainParts[0][0] + '*'.repeat(Math.max(1, domainParts[0].length - 1));
  const tld          = domainParts.slice(1).join('.');
  return `${maskedLocal}@${maskedDomain}.${tld}`;
}

/* ─── Icône SVG email ────────────────────────────────────────────────────────── */
function IconEmail() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke={COLORS.accent}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

/* ─── Icône SVG flèche retour ─────────────────────────────────────────────── */
function IconArrow() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

/* ─── Composant ─────────────────────────────────────────────────────────────── */

/**
 * EmailVerifyPanel
 *
 * @param {object}   props
 * @param {function} props.dispatch     - dispatch de useWalletStateMachine
 * @param {string}   props.apiBaseUrl   - URL de base de l'API (non utilisé directement :
 *                                        AuthService.js lit process.env.REACT_APP_SERVER)
 * @param {object}   props.pendingUser  - user retourné par email-signup (isVerified=false)
 *                                        { id, email, username, metamask, accessToken, ... }
 */
export function EmailVerifyPanel({ dispatch, pendingUser }) {
  /* Champ de saisie du code */
  const [code,        setCode]        = useState('');
  const [verifyError, setVerifyError] = useState(null);
  const [loading,     setLoading]     = useState(false);

  /* État de confirmation de renvoi */
  const [resendLoading,  setResendLoading]  = useState(false);
  const [resendConfirm,  setResendConfirm]  = useState(false);
  const [resendError,    setResendError]    = useState(null);

  /* Email masqué à afficher */
  const maskedEmail = pendingUser?.email ? maskEmail(pendingUser.email) : null;

  /* Adresse metamask du compte EarniConnect (utilisée par verify/resend) */
  const metamask = pendingUser?.metamask ?? null;

  /* ── Vérification du code ──────────────────────────────────────────────── */
  const handleVerify = useCallback(async () => {
    setVerifyError(null);
    const trimmed = code.trim();
    if (!trimmed) {
      setVerifyError('Please enter the verification code.');
      return;
    }
    if (!metamask) {
      setVerifyError('Account information missing. Please try again.');
      return;
    }

    setLoading(true);
    try {
      /* Appel au service existant (§9 — zéro doublon) :
         POST /auth/verify { metamask, code, isRegister: true }
         Retourne le user avec isVerified: true + accessToken mis à jour. */
      const user = await verify({ metamask, code: trimmed, isRegister: true });

      /* Si le backend renvoie un user vérifié, on dispatche EMAIL_VERIFIED */
      dispatch({ type: ACTION_TYPES.EMAIL_VERIFIED, payload: user });
    } catch (err) {
      setVerifyError(err?.message ?? 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [code, metamask, dispatch]);

  /* ── Renvoi du code ────────────────────────────────────────────────────── */
  const handleResend = useCallback(async () => {
    setResendError(null);
    setResendConfirm(false);
    if (!metamask) {
      setResendError('Account information missing. Please try again.');
      return;
    }

    setResendLoading(true);
    try {
      /* Appel au service existant (§9 — zéro doublon) :
         POST /auth/resend { metamask } */
      await resend({ metamask });
      setResendConfirm(true);
    } catch (err) {
      setResendError(err?.message ?? 'Failed to resend the code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  }, [metamask]);

  /* ── Retour vers EMAIL_REGISTER ────────────────────────────────────────── */
  const handleBack = useCallback(() => {
    dispatch({ type: ACTION_TYPES.BACK });
  }, [dispatch]);

  /* ── Soumission au clavier ─────────────────────────────────────────────── */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleVerify();
  }, [handleVerify]);

  return (
    <>
      <WalletBody>
        {/* Icône + titre */}
        <StateIconWrap style={{ paddingTop: SPACE.md, paddingBottom: SPACE.xs }}>
          <IconEmail />
          <StateTitle>Verify your email</StateTitle>
          <StateMessage>
            Enter the verification code sent to your email address.
          </StateMessage>
        </StateIconWrap>

        {/* Email masqué */}
        {maskedEmail && (
          <MaskedEmail aria-label={`Verification code sent to ${maskedEmail}`}>
            {maskedEmail}
          </MaskedEmail>
        )}

        {/* Champ de saisie du code */}
        <FormGroup>
          <FormLabel htmlFor="ewm-verify-code">
            Enter the verification code
          </FormLabel>
          <FormInput
            id="ewm-verify-code"
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e) => {
              /* N'accepter que les chiffres et espaces (OTP peut avoir des tirets selon implémentation) */
              setCode(e.target.value);
              setVerifyError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder="123456"
            autoComplete="one-time-code"
            disabled={loading}
            $hasError={!!verifyError}
            aria-describedby={verifyError ? 'ewm-verify-error' : undefined}
          />
        </FormGroup>

        {/* Erreur de vérification */}
        {verifyError && (
          <InlineError id="ewm-verify-error" role="alert">
            {verifyError}
          </InlineError>
        )}

        {/* Confirmation de renvoi */}
        {resendConfirm && (
          <AlertBox $type="info" role="status">
            <AlertText $type="info">
              A new code has been sent. Check your inbox (and spam folder).
            </AlertText>
          </AlertBox>
        )}

        {/* Erreur de renvoi */}
        {resendError && (
          <InlineError role="alert">{resendError}</InlineError>
        )}

        {/* Lien retour */}
        <BackLink
          type="button"
          onClick={handleBack}
          aria-label="Back to registration"
        >
          <IconArrow /> Back
        </BackLink>
      </WalletBody>

      <WalletFooter>
        <FooterRow>
          {/* Resend code */}
          <SecondaryBtn
            type="button"
            onClick={handleResend}
            disabled={resendLoading || loading}
            aria-busy={resendLoading}
            style={{ flex: 1 }}
          >
            {resendLoading ? 'Sending…' : 'Resend code'}
          </SecondaryBtn>

          {/* Verify */}
          <PrimaryBtn
            type="button"
            onClick={handleVerify}
            disabled={loading || !code.trim()}
            aria-busy={loading}
            style={{ flex: 2 }}
          >
            {loading ? 'Verifying…' : 'Verify'}
          </PrimaryBtn>
        </FooterRow>
      </WalletFooter>
    </>
  );
}

export default EmailVerifyPanel;
